import { Suspense, useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { useNubsGameRefresher, useNubsTaskRefresher, useVideoRefresher } from "../Global.state";
import { useUserQuery } from "../monitor/User.state";
import useRefresh, {
  dashboardQueryKey,
  challengesQueryKey,
  gameUri,
  leaderboardQueryKey,
  rewardsQueryKey,
  teamsQueryKey,
  groupsQueryKey,
  gameStreamQueryKey,
  personalizationsUri,
  analyticsQueryKey,
  infiniteAnswerCommentsQueryKey,
  nubsQueryKey,
  npcsQueryKey,
  judgeQueryKey,
} from "../lib/refresh";
import RefreshConflictModal from "./RefreshConflictModal";
import ReelsNotificationModal from "../reels/ReelsNotificationModal";
import { INFINITE_QUERY_KEY } from "../consts/request";
import { useDeleteAnswersCommentFromEventMutation, useLikeAnswersCommentFromEventMutation, useUnlikeAnswersCommentFromEventMutation } from "../monitor/Answer.state";
import { useMonitorQueryKey } from "../monitor/Monitor.state";
import { useRecoilState } from "recoil";
import { videoUuidState, wsMessagesBinState } from "./Refresher.state";
import { websocketState } from "./ConsoleUi/ConsoleUi.state";
import { uuidv4 } from "../lib/uuid";
import { signInState } from '../sign-in/SignIn.state'

const getGameId = ({ detail, source }) => {
  if (detail?.game) {
    return detail.game.id;
  }

  switch (source) {
    case "ss.answer.accept":
    case "ss.answer.create":
    case "ss.answer.feature":
    case "ss.answer.reject":
    case "ss.comment.create":
    case "ss.comment.delete":
    case "ss.comment.like":
    case "ss.comment.unlike":
      return detail.answer.game_id;
    case "ss.reward.create":
    case "ss.reward.delete":
    case "ss.reward.update":
      return detail.reward.game_id;
    default:
      return null;
  }
};

const refreshUris = {
  "ss.answer.accept": ['leaderboardQueryKey'],
  "ss.answer.create": ['gameStreamQueryKey', 'leaderboardQueryKey', 'monitorQueryKey'],
  "ss.answer.feature": ['leaderboardQueryKey'],
  "ss.answer.reject": ['leaderboardQueryKey'],
  "ss.answer.update": [],
  "ss.dropbox.connected": ['gameUri'],
  "ss.game.clone": ['dashboardQueryKey'],
  "ss.game.delete": ['dashboardQueryKey'],
  "ss.game.join": ['teamsQueryKey', 'gameStreamQueryKey'],
  "ss.game.media": ['nubsQueryKey', 'judgeQueryKey'],
  "ss.game.reel": ['gameUri'],
  "ss.game.rescore": ['leaderboardQueryKey'],
  "ss.game.score": ['leaderboardQueryKey'],
  "ss.game.update": ['dashboardQueryKey', 'gameUri'],
  "ss.reward.create": ['rewardsQueryKey'],
  "ss.reward.delete": ['rewardsQueryKey'],
  "ss.reward.update": ['rewardsQueryKey'],
  "ss.task.create": ['challengesQueryKey'],
  "ss.task.delete": ['challengesQueryKey'],
  "ss.task.reel": ['gameUri'],
  "ss.task.update": ['challengesQueryKey'],
  "ss.team.create": ['teamsQueryKey'],
  "ss.team.delete": ['teamsQueryKey'],
  "ss.team.merge": ['teamsQueryKey'],
  "ss.team.photo": ['teamsQueryKey'],
  "ss.team.reel": ['gameUri', 'gameStreamQueryKey'],
  "ss.team.rename": ['teamsQueryKey'],
  "ss.team.unmerge": ['teamsQueryKey'],
  "ss.team.update": ['teamsQueryKey'],
  "ss.personalization.delete": ['personalizationsUri'],
  "ss.personalization.update": ['personalizationsUri'],
  "ss.personalization.create": ['personalizationsUri'],
  "ss.group.create": ['groupsQueryKey'],
  "ss.group.delete": ['groupsQueryKey'],
  "ss.group.update": ['groupsQueryKey'],
  "ss.analytic.teams": ['analyticsQueryKey'],
  "ss.comment.create": ['infiniteAnswerCommentsQueryKey'],
  "ss.comment.delete": [],
  "ss.comment.like": [],
  "ss.comment.unlike": [],
  "ss.npc.create": ['npcsQueryKey'],
  "ss.npc.update": ['npcsQueryKey'],
  "ss.npc.delete": ['npcsQueryKey'],
};

const mutations = {
  "ss.game.media": [useVideoRefresher, useNubsTaskRefresher, useNubsGameRefresher],
  "ss.comment.delete": [useDeleteAnswersCommentFromEventMutation],
  "ss.comment.like": [useLikeAnswersCommentFromEventMutation],
  "ss.comment.unlike": [useUnlikeAnswersCommentFromEventMutation],
};

const Mutation = ({ game_id, task_id, message, useMutation }) => {
  const mutate = useMutation(game_id, (task_id || message?.detail?.media?.relation?.id), message);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => mutate(message), []);
  return null;
};

const RefreshUris = ({ game_id, source, message, shouldInvalidate = true }) => {
  const queryClient = useQueryClient();
  const monitorQueryKey = useMonitorQueryKey(game_id)
  const [wsMessageBin, setWsMessageBin] = useRecoilState(wsMessagesBinState);

  useEffect(() => {
    if (game_id && game_id !== "new" && shouldInvalidate) {
      for (let uriFn of refreshUris[source] || []) {
        // Query keys are built differently for each query states;
        // their parameters do not alway match with uriFn signatures (game_id, message),
        // thus it needs to be normalized manually if necessary.
        const uriFnNormalized = {
          monitorQueryKey,
          leaderboardQueryKey: leaderboardQueryKey(game_id),
          dashboardQueryKey: dashboardQueryKey(),
          teamsQueryKey: teamsQueryKey(game_id),
          rewardsQueryKey: rewardsQueryKey(game_id),
          challengesQueryKey: challengesQueryKey(game_id),
          groupsQueryKey: groupsQueryKey(game_id),
          gameUri: gameUri(game_id),
          gameStreamQueryKey: gameStreamQueryKey(game_id),
          personalizationsUri: personalizationsUri(game_id),
          analyticsQueryKey: analyticsQueryKey(game_id),
          infiniteAnswerCommentsQueryKey: infiniteAnswerCommentsQueryKey(null, message),
          nubsQueryKey: nubsQueryKey(game_id),
          npcsQueryKey: npcsQueryKey(game_id),
          judgeQueryKey: judgeQueryKey(game_id),
      }
        let key = uriFnNormalized[uriFn];
        if (!key && typeof uriFn === 'function') {
            key = uriFn(game_id, message)
        }

        // any events related to an infinite query keys
        // need to only invalidate a specific page in the collection.
        // otherwise it could unnecessarily load a whole list of pages to refresh the infinite query data.
        if (key.includes(INFINITE_QUERY_KEY)) {
            switch (true) {
                case source.includes('create'):
                    // Append new data to the first page of the collection
                    queryClient.invalidateQueries(key, {
                        refetchPage: (_, index) => {
                            return index === 0
                        }
                    })
                    break
                default:
                    // Do nothing
                    break
            }
        } else {
            // invalidate query and refetch new data from the server
            queryClient.invalidateQueries(key);
        }
        setWsMessageBin([
            ...wsMessageBin,
            message?.id,
        ])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game_id, queryClient, source]);

  return null;
};

const RefresherInstance = ({ id, reelScreen, subscriptions }) => {
  const { game_id } = useParams();
  const message = useRefresh({ subscriptions: Object.keys(refreshUris).join(",") });
  const keys = subscriptions ? subscriptions.split(",") : [];
  const [usedId, setUsedId] = useState();
  const [show, setShow] = useState(true);
  const [confirm, setConfirm] = useState(false);
  const [reel, setReel] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [wsMessageBin] = useRecoilState(wsMessagesBinState);
  const [wsMessage, setWsMessage] = useRecoilState(websocketState);
  const [, setVideoUuid] = useRecoilState(videoUuidState);

  const onCloseReelModal = () => {
    setReel(false);
    setVideoUuid(uuidv4());
  }

  useEffect(() => {
    setShow(true);

    if (
      reelScreen &&
      ["ss.game.reel", "ss.task.reel", "ss.team.reel"].includes(
        message.source
      ) &&
      message.detail.game.id === parseInt(game_id)
    ) {
      setReel(true);
    }
    if (message.detail) {
        setWsMessage([
            ...wsMessage,
            message,
        ])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message]);

  const onRefresh = () => {
    setConfirm(true);
    setShow(false);
  };

  if (message.id) {
    const userId = message.detail?.user?.id;
    const gameId = getGameId(message);
    const taskId = message.detail?.media?.relation?.id
    const m = mutations[message.source] || [];
    if (
      id &&
      gameId === parseInt(game_id) &&
      userId &&
      userId !== id &&
      keys.includes(message.source) &&
      refreshUris[message.source] &&
      message.id !== usedId &&
      !conflict
    ) {
      setUsedId(message.id);
      setConflict(true);
    }

    if (
      conflict &&
      keys.includes(message.source) &&
      refreshUris[message.source]
    ) {
      if (confirm) {
        window.requestAnimationFrame(() => {
          setConfirm(false);
          setConflict(false);
        });

        return (
          <>
            <RefreshUris
              game_id={game_id}
              key={message.id}
              source={message.source}
              message={message}
            />
            {parseInt(game_id) === gameId &&
              m.map((mutation) => (
                <Mutation
                  game_id={game_id}
                  task_id={taskId}
                  key={message.id}
                  message={message}
                  useMutation={mutation}
                />
              ))}
            {localStorage.getItem("reels_playing") ? (
              ""
            ) : (
              <ReelsNotificationModal
                game={message.detail?.game}
                onDismiss={onCloseReelModal}
                show={reel}
                task={message.detail?.task}
                team={message.detail?.team}
              />
            )}
          </>
        );
      } else {
        return (
          <RefreshConflictModal
            show={show}
            onConfirm={onRefresh}
            onDismiss={() => {
              setShow(false);
              setConflict(false);
            }}
          />
        );
      }
    }

    if (!keys.includes(message.source)) {
      const isCurrentGame = parseInt(gameId) === parseInt(game_id)
      const isFreshMessage = !wsMessageBin.includes(message.id)
      return (
        <>
          <RefreshUris
            game_id={game_id}
            key={message.id}
            source={message.source}
            message={message}
            shouldInvalidate={
              /**
               * Queries should invalidate on websocket events
               * only when it is coming from the same game
               * and it has not been consumed by the app
               */
              isCurrentGame &&
              isFreshMessage
            }
          />
          {isCurrentGame &&
            m.map((mutation, mutationIndex) => (
              <Mutation
                game_id={game_id}
                key={`${message.id}-${mutationIndex}`}
                message={message}
                useMutation={mutation}
              />
            ))}
          {localStorage.getItem("reels_playing") ? (
            ""
          ) : (
            <ReelsNotificationModal
              game={message.detail?.game}
              onDismiss={onCloseReelModal}
              show={reel}
              task={message.detail?.task}
              team={message.detail?.team}
            />
          )}
        </>
      );
    }
  }

  return null;
};

const Noop = () => null;

const Refresher = ({ reelScreen, subscriptions, uri }) => {
  const { id } = useUserQuery();
  const [key] = useRecoilState(signInState);

  return (
    <Suspense fallback={<Noop />}>
      <RefresherInstance
        id={id}
        key={key}
        reelScreen={reelScreen}
        subscriptions={subscriptions}
        uri={uri}
      />
    </Suspense>
  );
};

export default Refresher;